import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, Settings, Loader2,
  LayoutDashboard, FileText, Workflow, CheckSquare, Folder,
  Calendar, DollarSign, BarChart3, History, Users, HelpCircle,
  Database, ShieldCheck
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSystemMenuSync } from '@/hooks/useSystemMenuSync';
import '@/styles/system-navbar.css';
import { useToast } from '@/components/ui/use-toast';

const FALLBACK_MENU = [
  { label: 'Dashboard', icon: 'LayoutDashboard', route: '/sistema' },
  { label: 'Cadastros', icon: 'Database', route: '/sistema/cadastros' },
  { label: 'Processos', icon: 'FileText', route: '/sistema/processos' },
  { label: 'Tarefas', icon: 'CheckSquare', route: '/sistema/tarefas' },
  { label: 'Documentos', icon: 'Folder', route: '/sistema/documentos' },
  { label: 'Agenda', icon: 'Calendar', route: '/sistema/agenda' },
  { label: 'Financeiro', icon: 'DollarSign', route: '/sistema/financeiro' },
  { label: 'Relatórios', icon: 'BarChart3', route: '/sistema/relatorios' },
  { label: 'Histórico', icon: 'History', route: '/sistema/historico' },
  { label: 'Auditoria', icon: 'ShieldCheck', route: '/sistema/auditoria' },
  { label: 'Especialistas', icon: 'Users', route: '/sistema/multiespecialistas' },
  { label: 'Ajuda', icon: 'HelpCircle', route: '/sistema/ajuda' }
];

const iconMap = {
  LayoutDashboard, FileText, Workflow, CheckSquare, Folder,
  Calendar, DollarSign, BarChart3, History, Users, HelpCircle, Database, ShieldCheck
};

export default function SystemNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentLanguage } = useTranslation();
  const { toast } = useToast();
  const { menuItems, loading } = useSystemMenuSync();

  const getPath = (path) => `/${currentLanguage}${path}`;
  const getIcon = (iconName) => iconMap[iconName] || HelpCircle;

  const handleActionClick = () => {
    toast({
      title: "Em desenvolvimento",
      description: "🚧 This feature isn't implemented yet—but don't worry! You can request it in your next prompt! 🚀",
    });
  };

  const displayMenu = menuItems?.length > 0 ? menuItems : FALLBACK_MENU;

  return (
    <nav className="system-navbar bg-[#193D6D] text-white shadow-md border-b-2 border-[#FCD34D]">
      <div className="system-navbar-container overflow-x-auto scrollbar-hide">
        <div className="navbar-menu min-w-max">
          {loading && menuItems?.length === 0 ? (
            <div className="flex items-center justify-center px-4 py-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#FCD34D]" />
            </div>
          ) : (
            displayMenu.map((item, index) => {
              const Icon = getIcon(item.icon);
              const itemRoute = item.route.startsWith('/') ? item.route : `/${item.route}`;
              const isExact = itemRoute === '/sistema';

              // Highlight if we are anywhere inside the route's base path
              const isActive = isExact
                ? location.pathname === getPath(itemRoute) || location.pathname === getPath(itemRoute) + '/'
                : location.pathname.startsWith(getPath(itemRoute.split('/')[1] === 'sistema' ? '/sistema/' + itemRoute.split('/')[2] : itemRoute));

              return (
                <button
                  key={item.id || index}
                  onClick={() => navigate(getPath(itemRoute))}
                  className={`menu-item transition-all duration-300 relative bg-transparent border-none cursor-pointer flex items-center gap-2 ${isActive ? 'text-[#FCD34D] bg-[rgba(252,211,77,0.1)] font-semibold' : 'text-white hover:text-[#FCD34D] hover:bg-[rgba(255,255,255,0.1)]'}`}
                  title={item.label}
                >
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
                  <span>{item.label}</span>
                  {isActive && <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#FCD34D] rounded-t-md" />}
                </button>
              );
            })
          )}
        </div>

        <div className="navbar-right-actions border-l border-[rgba(255,255,255,0.2)] ml-auto pl-4">
          <button onClick={handleActionClick} className="action-icon-btn text-white hover:text-[#FCD34D] hover:bg-[rgba(255,255,255,0.1)]" title="Notificações">
            <Bell className="w-5 h-5" />
          </button>
          <button onClick={handleActionClick} className="action-icon-btn text-white hover:text-[#FCD34D] hover:bg-[rgba(255,255,255,0.1)]" title="Configurações">
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
    </nav>
  );
}